import React from 'react';
import PublicLayout from '../../components/public/PublicLayout';
import { TrendingUp, Bell, FileText } from 'lucide-react';

const PolicyDevelopmentsPage = () => {
  const developments = [
    {
      title: 'National Licensure Framework Initiative',
      date: '2024',
      status: 'In Progress',
      description: 'Working with governments across Africa to establish standardized licensure requirements for respiratory therapists.',
      impact: 'High',
      partners: ['Ministries of Health', 'National Medical Councils']
    },
    {
      title: 'Respiratory Care Scope of Practice Advocacy',
      date: '2024',
      status: 'Active',
      description: 'Advocacy efforts to formally recognize respiratory therapy as a distinct healthcare profession in African countries.',
      impact: 'High',
      partners: ['Healthcare Regulatory Bodies', 'Professional Associations']
    },
    {
      title: 'Clinical Competency Standards Development',
      date: '2023-2024',
      status: 'In Development',
      description: 'Creating pan-African clinical competency standards for respiratory therapy education and practice.',
      impact: 'Medium',
      partners: ['Educational Institutions', 'Clinical Experts']
    },
    {
      title: 'Equipment Procurement Guidelines',
      date: '2024',
      status: 'Draft Stage',
      description: 'Developing guidelines for respiratory equipment procurement, quality standards, and maintenance protocols.',
      impact: 'Medium',
      partners: ['Equipment Manufacturers', 'Hospital Administrators']
    },
    {
      title: 'Infection Prevention & Control Standards',
      date: '2023',
      status: 'Under Review',
      description: 'Updated infection control protocols specific to respiratory therapy practice in resource-limited settings.',
      impact: 'High',
      partners: ['WHO', 'CDC Africa', 'Infection Control Experts']
    }
  ];

  const advocacyPriorities = [
    {
      icon: '🎓',
      title: 'Education Standardization',
      description: 'Establishing minimum education requirements for respiratory therapy programs across Africa.'
    },
    {
      icon: '📜',
      title: 'Professional Recognition',
      description: 'Securing legal recognition of respiratory therapy as a distinct healthcare profession.'
    },
    {
      icon: '💼',
      title: 'Workforce Development',
      description: 'Creating career pathways and professional development frameworks for respiratory therapists.'
    },
    {
      icon: '🏥',
      title: 'Healthcare Integration',
      description: 'Integrating respiratory therapy services into national healthcare systems and policies.'
    }
  ];

  return (
    <PublicLayout>
      <div className="py-5">
        <div className="container">
          {/* Header */}
          <div className="row justify-content-center text-center mb-5">
            <div className="col-lg-8">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4" 
                   style={{ width: '80px', height: '80px', backgroundColor: '#6B8E23' }}>
                <TrendingUp className="text-white" size={40} />
              </div>
              <h1 className="display-4 fw-bold text-dark mb-3">Policy Developments</h1>
              <p className="lead text-muted">
                Latest policy updates and advocacy initiatives advancing respiratory care in Africa
              </p>
            </div>
          </div>

          {/* Current Developments */}
          <div className="row justify-content-center mb-5">
            <div className="col-lg-10">
              <h3 className="h4 fw-bold text-dark mb-4">
                <Bell className="me-2" style={{ color: '#6B8E23' }} size={24} />
                Current Policy Initiatives
              </h3>
              
              <div className="row g-4">
                {developments.map((dev, index) => (
                  <div key={index} className="col-12">
                    <div className="card shadow-sm border-0">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div className="flex-grow-1">
                            <h5 className="fw-bold text-dark mb-2">{dev.title}</h5>
                            <p className="text-muted mb-3">{dev.description}</p>
                            <div className="d-flex flex-wrap gap-2 align-items-center">
                              <span className={`badge ${dev.status === 'In Progress' || dev.status === 'Active' ? 'text-white' : 'bg-warning text-dark'}`}
                                    style={dev.status === 'In Progress' || dev.status === 'Active' ? { backgroundColor: '#6B8E23' } : {}}>
                                {dev.status}
                              </span>
                              <span className="badge bg-secondary">{dev.date}</span>
                              <span className={`badge ${dev.impact === 'High' ? 'bg-danger' : 'bg-info'}`}>
                                {dev.impact} Impact
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-top">
                          <p className="small text-muted mb-1"><strong>Key Partners:</strong></p>
                          <p className="small text-muted mb-0">{dev.partners.join(' • ')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Advocacy Priorities */}
          <div className="row justify-content-center mb-5">
            <div className="col-lg-10">
              <h3 className="h4 fw-bold text-dark mb-4">Our Advocacy Priorities</h3>
              
              <div className="row g-4">
                {advocacyPriorities.map((priority, index) => (
                  <div key={index} className="col-md-6">
                    <div className="card h-100 shadow-sm border-0">
                      <div className="card-body p-4">
                        <div className="d-flex align-items-start">
                          <div className="me-3" style={{ fontSize: '2rem' }}>
                            {priority.icon}
                          </div>
                          <div>
                            <h5 className="fw-bold text-dark mb-2">{priority.title}</h5>
                            <p className="text-muted mb-0">{priority.description}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Get Involved Section */}
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-5">
                  <h3 className="h4 fw-bold text-dark mb-4">Get Involved in Policy Development</h3>
                  <div className="row g-4">
                    <div className="col-md-6">
                      <h5 className="fw-bold text-dark mb-3">
                        <i className="fas fa-comments me-2" style={{ color: '#6B8E23' }}></i>
                        Share Your Input
                      </h5>
                      <p className="text-muted">
                        ARCA members can participate in policy consultations, provide feedback on draft 
                        documents, and share experiences from their practice settings.
                      </p>
                    </div>
                    <div className="col-md-6">
                      <h5 className="fw-bold text-dark mb-3">
                        <i className="fas fa-envelope me-2" style={{ color: '#6B8E23' }}></i>
                        Stay Informed
                      </h5>
                      <p className="text-muted">
                        Subscribe to our policy updates newsletter to receive notifications about new 
                        initiatives, consultation opportunities, and policy achievements.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="card shadow border-0" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)' }}>
                <div className="card-body p-5 text-center text-white">
                  <h3 className="h4 fw-bold mb-3">Join Our Advocacy Efforts</h3>
                  <p className="mb-4">
                    Be part of the movement to advance respiratory care policy across Africa. 
                    Your voice matters in shaping the future of our profession.
                  </p>
                  <div className="d-flex flex-wrap justify-content-center gap-3">
                    <a href="/get-involved/become-member" className="btn btn-light btn-lg fw-bold">Become a Member</a>
                    <a href="/contact" className="btn btn-outline-light btn-lg">Contact Policy Team</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default PolicyDevelopmentsPage;
